import { useState } from 'react';

// Centralized image imports
import learningIcon from '../assets/images/action_learn_ready.webp';
import doneIcon from '../assets/images/action_learn_done.webp';
import reviewIcon from '../assets/images/action_review_ready.webp';
import boredIcon from '../assets/images/action_review_done.webp';
import vacationImg from '../assets/images/action_vacation.webp';
import correctIcon from '../assets/images/action_correct.webp';
import incorrectIcon from '../assets/images/action_incorrect.webp';
import repairIcon from '../assets/images/action_repair.webp';
import skipIcon from '../assets/images/action_skip.webp';
import noInternetImg from '../assets/images/action_no_internet.webp';
import home0 from '../assets/images/home0.webp';
import home1 from '../assets/images/home1.webp';
import home2 from '../assets/images/home2.webp';
import home3 from '../assets/images/home3.webp';
import logo from '../assets/images/logo.svg';
import milestone1 from '../assets/images/milestone_1.webp';
import milestone2 from '../assets/images/milestone_2.webp';
import milestone3 from '../assets/images/milestone_3.webp';
import milestone4 from '../assets/images/milestone_4.webp';
import milestone5 from '../assets/images/milestone_5.webp';
import profileImg from '../assets/images/profile.webp';
import spinnerImg from '../assets/images/spinner.webp';
import sadImg from '../assets/images/404sad.webp';
import robotImg from '../assets/images/robot.webp';

// Image registry with metadata
export const IMAGES = {
  // Action icons
  ACTION_LEARN_READY: { src: learningIcon, alt: 'Ready to learn', priority: 'high' },
  ACTION_LEARN_DONE: { src: doneIcon, alt: 'Learning complete', priority: 'high' },
  ACTION_REVIEW_READY: { src: reviewIcon, alt: 'Ready to review', priority: 'high' },
  ACTION_REVIEW_DONE: { src: boredIcon, alt: 'Review complete', priority: 'high' },

  ACTION_VACATION: { src: vacationImg, alt: 'On vacation', priority: 'medium' },
  ACTION_CORRECT: { src: correctIcon, alt: 'Correct answer', priority: 'low' },
  ACTION_INCORRECT: { src: incorrectIcon, alt: 'Incorrect answer', priority: 'low' },
  ACTION_REPAIR: { src: repairIcon, alt: 'Repair action', priority: 'low' },
  ACTION_SKIP: { src: skipIcon, alt: 'Skip action', priority: 'low' },
  ACTION_NO_INTERNET: { src: noInternetImg, alt: 'No internet connection', priority: 'low' },

  // Home images
  HOME_0: { src: home0, alt: 'Home illustration 1', priority: 'high' },
  HOME_1: { src: home1, alt: 'Home illustration 2', priority: 'medium' },
  HOME_2: { src: home2, alt: 'Home illustration 3', priority: 'medium' },
  HOME_3: { src: home3, alt: 'Home illustration 4', priority: 'medium' },

  // Milestones
  MILESTONE_1: { src: milestone1, alt: 'Milestone 1', priority: 'medium' },
  MILESTONE_2: { src: milestone2, alt: 'Milestone 2', priority: 'medium' },
  MILESTONE_3: { src: milestone3, alt: 'Milestone 3', priority: 'medium' },
  MILESTONE_4: { src: milestone4, alt: 'Milestone 4', priority: 'medium' },
  MILESTONE_5: { src: milestone5, alt: 'Milestone 5', priority: 'medium' },

  // UI elements
  LOGO: { src: logo, alt: 'Kikikata logo', priority: 'high' },
  PROFILE: { src: profileImg, alt: 'Profile image', priority: 'high' },
  SPINNER: { src: spinnerImg, alt: 'Loading spinner', priority: 'high' },
  NOT_FOUND: { src: sadImg, alt: '404 error illustration', priority: 'low' },
  CONNECTION_BLOCKED: { src: robotImg, alt: 'A robot fox and a fox pointing at each other', priority: 'low' },
};

const fetchPriorityMap = { high: 'high', medium: 'auto', low: 'low' };

/**
 * Warm the browser (and, in production, the service worker runtime cache)
 * with every registered image so navigation never shows image pop-in and
 * failure-state illustrations (e.g. the offline overlay's) are available
 * when the network is broken — by then they can no longer be fetched.
 *
 * Downloads run tier by tier (all high in parallel, then medium, then low),
 * starting after page load plus a delay, so the visible screen's images are
 * cached first and startup requests are never competed with. Already-rendered
 * images resolve instantly from cache.
 */
export const preloadAllImages = () => {
  const loadTier = (priority) =>
    Promise.all(
      Object.values(IMAGES)
        .filter((imageData) => imageData.priority === priority)
        .map(
          (imageData) =>
            new Promise((resolve) => {
              const img = new Image();
              img.onload = resolve;
              img.onerror = resolve; // keep going — render-time fetch can still retry
              img.src = imageData.src;
            })
        )
    );

  const load = async () => {
    await loadTier('high');
    await loadTier('medium');
    await loadTier('low');
  };

  if (document.readyState === 'complete') {
    setTimeout(load, 2000);
  } else {
    window.addEventListener('load', () => setTimeout(load, 2000), { once: true });
  }
};

export const OptimizedImage = ({ imageKey, className = '', style, fallback = null, ...props }) => {
  const [hasError, setHasError] = useState(false);
  const imageData = IMAGES[imageKey];

  if (!imageData || hasError) {
    return fallback;
  }

  return (
    <img
      src={imageData.src}
      alt={imageData.alt}
      className={className}
      style={style}
      decoding="async"
      fetchPriority={fetchPriorityMap[imageData.priority]}
      loading={imageData.priority === 'low' ? 'lazy' : 'eager'}
      onError={() => setHasError(true)}
      {...props}
    />
  );
};